import { useState, useEffect, useRef } from "react";
import "../App.css";
import { randomTilt } from "./Select.tsx";
import { SliderHandle } from "./SliderHandle.tsx";

interface RangeSliderProps {
  min: number;
  max: number;
  low: number;
  high: number;
  // Called on release, like the single sliders, so dragging doesn't spam
  onCommit: (low: number, high: number) => void;
}

// Two range inputs stacked on one white bar. The thumbs can meet but not
// cross; the pink fill between them shows the chosen span.
export const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  low,
  high,
  onCommit,
}) => {
  const [lo, setLo] = useState(low);
  const [hi, setHi] = useState(high);
  // Each thumb is a sticker with its own angle, re-rolled on release
  const [tilts, setTilts] = useState(() => [randomTilt(), randomTilt()]);
  const [grabbed, setGrabbed] = useState<"lo" | "hi" | null>(null);

  useEffect(() => {
    setLo(low);
    setHi(high);
  }, [low, high]);

  // Share of the track, 0 to 1. A thumb is 44px wide, so its centre travels
  // from 22px to 100% - 22px.
  const share = (v: number) => (v - min) / (max - min);
  // When the thumbs sit on top of each other, keep the one that can still
  // move on top: low near the right end, high everywhere else.
  const lowOnTop = lo === hi && lo > (min + max) / 2;
  // When the thumbs meet, whichever is on top may need to move the other
  // one (dragging left from the top "high" thumb moves "low"). Set on the
  // first such move and held until release.
  const steer = useRef<"lo" | "hi" | null>(null);
  const changeLo = (v: number) => {
    if (steer.current === "hi" || (lo === hi && v > hi)) {
      steer.current = "hi";
      setHi(Math.max(v, lo));
    } else setLo(Math.min(v, hi));
  };
  const changeHi = (v: number) => {
    if (steer.current === "lo" || (lo === hi && v < lo)) {
      steer.current = "lo";
      setLo(Math.min(v, hi));
    } else setHi(Math.max(v, lo));
  };
  const commit = () => {
    steer.current = null;
    setGrabbed(null);
    setTilts(([lo, hi]) => [randomTilt(lo), randomTilt(hi)]);
    onCommit(lo, hi);
  };

  // The handle that is actually moving: the grabbed one, or the other one
  // when the thumbs met and the drag is steering it
  const moving = steer.current ?? grabbed;

  return (
    <div className="range-slider">
      <div
        className="range-slider-fill"
        style={{
          left: `calc(22px + (100% - 44px) * ${share(lo)})`,
          width: `calc((100% - 44px) * ${share(hi) - share(lo)})`,
        }}
      />
      <input
        className="outset-slider range-slider-input"
        style={{ zIndex: lowOnTop ? 2 : 1 }}
        type="range"
        min={min}
        max={max}
        step={1}
        value={lo}
        onChange={(e) => changeLo(Number(e.target.value))}
        onPointerDown={() => setGrabbed("lo")}
        onPointerCancel={() => setGrabbed(null)}
        onMouseUp={commit}
        onTouchEnd={commit}
        onKeyUp={commit}
      />
      <input
        className="outset-slider range-slider-input"
        style={{ zIndex: lowOnTop ? 1 : 2 }}
        type="range"
        min={min}
        max={max}
        step={1}
        value={hi}
        onChange={(e) => changeHi(Number(e.target.value))}
        onPointerDown={() => setGrabbed("hi")}
        onPointerCancel={() => setGrabbed(null)}
        onMouseUp={commit}
        onTouchEnd={commit}
        onKeyUp={commit}
      />
      <SliderHandle
        share={share(lo)}
        label={String(lo)}
        tilt={tilts[0]}
        grabbed={moving === "lo"}
      />
      <SliderHandle
        share={share(hi)}
        label={String(hi)}
        tilt={tilts[1]}
        grabbed={moving === "hi"}
      />
    </div>
  );
};

export default RangeSlider;
